/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Soft UI Dashboard PRO React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that contains other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
  The `visible` show/hide a route from the menu
  The `dynamic` create dynamically a children
*/

import Wizard from "layouts/applications/wizard";
import TemplatesList from "views/templates-list";
import GeneralFiltersList from "views/general-filters-list";
import OddsSettingsList from "views/odds-settings-list";
import LandingPage from "views/landing-page";
import Login from "views/login";
import Notifications from "views/notifications";
import PlatformsList from "views/platforms/platforms-list";
import UsersList from "views/users/users-list";
import UserEdit from "views/users/edit-user";
import UsersLogsList from "views/users/users-logs";
import NetworkError from "views/errors/networkError";
import SportsTranslationsList from "views/translations/sports";
import RegionsTranslationsList from "views/translations/regions";
import CompetitionsTranslationsList from "views/translations/competitions";
import ParticipantsTranslationsList from "views/translations/participants";
import MarketsTranslationsList from "views/translations/markets";
import Account from "views/users/account";
import ApiDocumentation from "views/api-docs/documentation";
import FeedIntegration from "views/api-docs/feed-integration";
import ChangeLog from "views/api-docs/changelog";
import Settlementnew2 from "views/settlement/new2";
import SettlementOutrights from "views/settlement/outrights";
import CustomerView from "views/settlement/customerView";
import AdvancedSettlement from "views/settlement/advanced";
import FixtureSettlement from "views/settlement/fixture";
import OddSettlement from "views/settlement/odd";
import MarketsSettingsConfig from "views/markets/markets-config/list";
import ExportManager from "views/exports";
import Sportsbook from "views/sportsbook";
import Confirmation from "views/settlement/confirmation";
import Signals from "views/signals";
import RiskManagementCurrent from "views/risk-management/current";
import RiskManagement from "views/risk-management";

import Icon from "@mui/material/Icon";

import { Navigate } from "react-router-dom";
import FixtureView from "views/fixture-view";
import FixtureTraderView from "views/fixture-trader-view";
import StuckedFixturesList from "views/stucked-fixtures-list";

const routes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon>dashboard_outlined_icon </Icon>,
    visible: false,
    permission: "dashboard",
    route: "/",
    noCollapse: true,
    component: <Navigate to="/dashboard" />,
  },
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon>dashboard_outlined_icon </Icon>,
    visible: true,
    permission: "dashboard",
    route: "/dashboard",
    noCollapse: true,
    component: <LandingPage />,
  },
  {
    type: "collapse",
    name: "Notifications",
    key: "notifications",
    icon: <Icon>notifications_outlined_icon</Icon>,
    visible: false,
    permission: "notifications",
    route: "/notifications",
    noCollapse: true,
    component: <Notifications />,
  },
  {
    type: "collapse",
    name: "Management",
    key: "management",
    route: "management/platforms",
    icon: <Icon>view_in_ar_outlined_icon </Icon>,
    visible: true,
    permission: "management",
    collapse: [
      {
        type: "collapse",
        name: "Platforms",
        key: "platforms",
        route: "management/platforms",
        visible: true,
        permission: "management-platforms",
        icon: <Icon>web_outlined_icon</Icon>,
        component: <PlatformsList />,
      },
      {
        type: "collapse",
        name: "Users",
        key: "users",
        route: "management/users",
        visible: true,
        permission: "management-users",
        icon: <Icon>group_outlined_icon </Icon>,
        component: <UsersList />,
        collapse: [
          {
            type: "collapse",
            name: "Users List",
            key: "users-list",
            route: "management/users/users-list",
            visible: true,
            permission: "management-users",
            component: <UsersList />,
          },
          {
            type: "collapse",
            name: "Users Logs",
            key: "users-logs",
            route: "management/users/users-logs",
            visible: true,
            permission: "management-users-userlogs",
            component: <UsersLogsList />,
          },
        ],
      },
      {
        type: "collapse",
        name: "Users Edit",
        key: "users-edit",
        route: "management/users/edit/:id",
        visible: false,
        permission: "management-users",
        component: <UserEdit />,
      },
      {
        type: "collapse",
        name: "Translations",
        key: "translations",
        route: "management/translations/sports",
        icon: <Icon>g_translate_outlined_icon</Icon>,
        visible: true,
        permission: "management-translations",
        collapse: [
          {
            type: "collapse",
            name: "Sports",
            key: "sport",
            route: "management/translations/sports",
            visible: true,
            permission: "management-translations-sports",
            component: <SportsTranslationsList />,
          },
          {
            type: "collapse",
            name: "Regions",
            key: "regions",
            route: "management/translations/regions",
            visible: true,
            permission: "management-translations-regions",
            component: <RegionsTranslationsList />,
          },
          {
            type: "collapse",
            name: "Competitions",
            key: "competitions",
            route: "management/translations/competitions",
            visible: true,
            permission: "management-translations-competitions",
            component: <CompetitionsTranslationsList />,
          },
          {
            type: "collapse",
            name: "Participants",
            key: "participants",
            route: "management/translations/participants",
            visible: true,
            permission: "management-translations-participants",
            component: <ParticipantsTranslationsList />,
          },
          {
            type: "collapse",
            name: "Markets",
            key: "markets",
            route: "management/translations/markets",
            visible: true,
            permission: "management-translations-markets",
            component: <MarketsTranslationsList />,
          },
        ],
      },
      {
        type: "collapse",
        name: "Export",
        key: "export",
        route: "management/export/competitions",
        icon: <Icon>category_icon</Icon>,
        visible: true,
        permission: "management-export",
        collapse: [
          {
            type: "collapse",
            name: "Regions",
            key: "regions",
            route: "management/export/regions",
            visible: true,
            permission: "management-export-regions",
            component: <ExportManager dataSet="regions" />,
          },
          {
            type: "collapse",
            name: "Competitions",
            key: "competitions",
            route: "management/export/competitions",
            visible: true,
            permission: "management-export-competitions",
            component: <ExportManager dataSet="competitions" />,
          },
          {
            type: "collapse",
            name: "Participants",
            key: "participants",
            route: "management/export/participants",
            visible: true,
            permission: "management-export-participants",
            component: <ExportManager dataSet="participants" />,
          },
          {
            type: "collapse",
            name: "Markets",
            key: "markets",
            route: "management/export/markets",
            visible: true,
            permission: "management-export-markets",
            component: <ExportManager dataSet="markets" />,
          },
        ],
      },
      {
        type: "collapse",
        name: "Signals",
        key: "signals",
        route: "management/signals",
        icon: <Icon>cell_tower_icon</Icon>,
        visible: true,
        permission: "management-signals",
        component: <Signals />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Monitoring",
    key: "monitoring",
    icon: <Icon>display_settings_outlined_icon</Icon>,
    route: "/monitoring",
    visible: true,
    permission: "monitoring",
    noCollapse: true,
    component: <Sportsbook />,
  },
  {
    type: "collapse",
    name: "Monitoring",
    key: "monitoring",
    icon: <Icon>display_settings_outlined_icon</Icon>,
    route: "/monitoring/inplay/:sport",
    visible: false,
    permission: "monitoring",
    noCollapse: true,
    component: <Sportsbook />,
  },
  {
    type: "collapse",
    name: "Monitoring",
    key: "monitoring",
    icon: <Icon>display_settings_outlined_icon</Icon>,
    route: "/monitoring/pregame/:sport",
    visible: false,
    permission: "monitoring",
    noCollapse: true,
    component: <Sportsbook />,
  },
  {
    type: "collapse",
    name: "Monitoring",
    key: "monitoring",
    icon: <Icon>display_settings_outlined_icon</Icon>,
    route: "/monitoring/fixture-live-view",
    visible: false,
    permission: "monitoring",
    noCollapse: true,
    component: <FixtureView />,
  },
  {
    type: "collapse",
    name: "Monitoring",
    key: "monitoring",
    icon: <Icon>display_settings_outlined_icon</Icon>,
    route: "/monitoring/fixture-trader-view",
    visible: false,
    permission: "monitoring",
    noCollapse: true,
    component: <FixtureTraderView />,
  },
  {
    type: "collapse",
    name: "Feed Settings",
    key: "feed-settings",
    route: "feed-settings/templates",
    icon: <Icon>rss_feed_outlined_icon</Icon>,
    visible: true,
    permission: "feed-settings",
    collapse: [
      {
        name: "Templates",
        key: "templates",
        route: "feed-settings/templates",
        icon: <Icon>view_in_ar_outlined_icon </Icon>,
        visible: true,
        permission: "feed-settings-templates",
        component: <TemplatesList />,
      },
      {
        name: "Filters",
        key: "filters",
        route: "feed-settings/filters",
        icon: <Icon>tune_outlined_icon</Icon>,
        visible: true,
        permission: "feed-settings-filters",
        component: <GeneralFiltersList />,
      },
      {
        name: "Create Filter",
        key: "create-filter",
        route: "feed-settings/filters/create-filter",
        visible: false,
        permission: "feed-settings-filters",
        component: <Wizard mode={"create"} template={"0"} filterProvider={null} />,
      },
      {
        name: "Edit Filter",
        key: "edit-filter",
        route: "feed-settings/filters/:id/edit-filter",
        visible: false,
        permission: "feed-settings-filters",
        component: <Wizard mode={"edit"} template={"1"} filterProvider={null} />,
      },
      // {
      //   name: "Markets",
      //   key: "markets",
      //   route: "feed-settings/markets",
      //   icon: <Icon>price_change_outlined_icon</Icon>,
      //   visible: true,
      //   permission: "feed-settings-markets",
      //   component: <MarketsSettings />,
      // },
      {
        // type: "collapse",
        name: "Markets Settings",
        key: "markets-settings",
        route: "feed-settings/markets-settings",
        icon: <Icon>price_change_outlined_icon</Icon>,
        visible: true,
        permission: "feed-settings-markets",
        component: <MarketsSettingsConfig />,
        // collapse: [
        //   {
        //     name: "Markets Settings List",
        //     key: "markets-settings-list",
        //     route: "feed-settings/markets/markets-settings",
        //     visible: true,
        //     permission: "feed-settings-markets",
        //     component: <MarketsSettings />,
        //   },
        // ],
      },
      {
        name: "Odds Settings",
        key: "odds-settings",
        route: "feed-settings/odds",
        icon: <Icon>exposure_icon</Icon>,
        visible: true,
        permission: "feed-settings-odds-edit",
        component: <OddsSettingsList />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Settlement",
    key: "settlement",
    route: "settlement",
    icon: <Icon>rule_outlined_icon </Icon>,
    visible: true,
    permission: "settlement",
    collapse: [
      {
        name: "Current V2",
        key: "currentv2",
        route: "settlement/currentv2",
        icon: <Icon>update_icon</Icon>,
        visible: true,
        permission: "settlement-current",
        component: <Settlementnew2 />,
      },
      {
        name: "Outrights",
        key: "outrights",
        route: "settlement/outrights",
        icon: <Icon>update_icon</Icon>,
        visible: true,
        permission: "settlement-current",
        component: <SettlementOutrights />,
      },
      {
        name: "Selected Customer",
        key: "selectedcustomer",
        route: "settlement/selectedcustomer",
        icon: <Icon>support_agent_icon </Icon>,
        visible: true,
        permission: "settlement-current",
        component: <CustomerView />,
      },
      {
        name: "Odd",
        key: "odd",
        route: "settlement/odd",
        icon: <Icon>sell_icon</Icon>,
        visible: true,
        permission: "settlement-current",
        component: <OddSettlement />,
      },
      {
        name: "Advanced",
        key: "advanced",
        route: "settlement/advanced",
        icon: <Icon>saved_search_icon </Icon>,
        visible: true,
        permission: "settlement-advanced",
        component: <AdvancedSettlement />,
      },
      {
        name: "Stucked Fixtures",
        key: "stucked-fixtures",
        route: "settlement/stucked-fixtures",
        icon: <Icon>notifications_none_icon </Icon>,
        visible: true,
        permission: "settlement-current",
        component: <StuckedFixturesList />,
      },
      {
        name: "Fixture",
        key: "fixture",
        route: "settlement/fixture",
        icon: <Icon>stadium_icon</Icon>,
        visible: true,
        permission: "settlement-fixture",
        component: <FixtureSettlement />,
      },
      {
        name: "Confirmations",
        key: "confirmations",
        route: "settlement/confirmations",
        icon: <Icon>flaky_icon </Icon>,
        visible: true,
        permission: "settlement-fixture",
        component: <Confirmation />,
      },
    ],
  },
  {
    type: "collapse",
    name: "Risk Management",
    key: "riskmanagement",
    route: "riskmanagement",
    icon: <Icon>security_outlined_icon </Icon>,
    visible: true,
    permission: "riskmanagement",
    collapse: [
      {
        name: "Risky Betslips",
        key: "current",
        route: "riskmanagement/current",
        icon: <Icon>privacy_tip_outlined_icon </Icon>,
        visible: true,
        permission: "riskmanagement-current",
        component: <RiskManagementCurrent />,
      },
      {
        name: "Advanced",
        key: "advanced",
        route: "riskmanagement/advanced",
        icon: <Icon>policy_outlined_icon </Icon>,
        visible: true,
        permission: "riskmanagement-advanced",
        component: <RiskManagement />,
      },
    ],
  },
  {
    name: "networkError",
    key: "networkError",
    route: "/networkError",
    visible: false,
    permission: true,
    component: <NetworkError />,
  },
  {
    name: "login",
    key: "login",
    route: "/login",
    visible: false,
    permission: true,
    component: <Login />,
  },
  {
    name: "Api Documentation",
    key: "apiDocumentation",
    route: "/feed-api/documentation",
    visible: false,
    permission: true,
    component: <ApiDocumentation />,
  },
  {
    name: "Changelog",
    key: "changeLog",
    route: "/feed-api/changelog",
    visible: false,
    permission: true,
    component: <ChangeLog />,
  },
  {
    name: "Feed Integration",
    key: "feedIntegration",
    route: "/feed-api/feed-integration",
    visible: false,
    permission: true,
    component: <FeedIntegration />,
  },
  {
    type: "collapse",
    name: "Account",
    key: "account",
    icon: <Icon>manage_accounts_outlined_icon</Icon>,
    route: "/account",
    visible: true,
    permission: true,
    noCollapse: true,
    component: <Account />,
  },
  {
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon>dashboard_outlined_icon </Icon>,
    visible: false,
    permission: "dashboard",
    route: "*",
    component: <LandingPage />,
  },
];

export default routes;
